import { cn } from "@/utils/cn";
import { createContext, useContext, useLayoutEffect, useRef, useState } from "react";

const VisibleWidth = createContext(0);

/** The visible width of the nearest ScrollFade, for content that should span what the reader sees. */
export const useVisibleWidth = () => useContext(VisibleWidth);

/**
 * Scrolls sideways and fades the edge that has more to see, so a wide table reads as scrollable. The fades are
 * gradients laid over the scroller rather than a mask on it: a mask makes Chrome redraw the whole table as its own
 * layer on every scroll frame.
 */
export const ScrollFade = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState({ left: false, right: false, width: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const next = {
        left: el.scrollLeft > 1,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
        width: el.clientWidth,
      };
      setState((s) => (s.left === next.left && s.right === next.right && s.width === next.width ? s : next));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    el.addEventListener("scroll", update, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", update);
    };
  }, []);

  const fade = "pointer-events-none absolute bottom-0 top-0 z-[5] w-7 transition-opacity duration-150";
  return (
    <VisibleWidth.Provider value={state.width}>
      <div className="relative">
        <div
          ref={ref}
          data-hscroll
          data-fade-left={state.left || undefined}
          data-fade-right={state.right || undefined}
          className={cn("overflow-x-auto overscroll-x-contain", className)}
        >
          {children}
        </div>
        <div aria-hidden className={cn(fade, "left-0 bg-gradient-to-r from-white", !state.left && "opacity-0")} />
        <div aria-hidden className={cn(fade, "right-0 bg-gradient-to-l from-white", !state.right && "opacity-0")} />
      </div>
    </VisibleWidth.Provider>
  );
};
